import { apiService } from './api';

export interface BackupInfo {
  name: string;
  size: number;
  createdAt: string;
  /** null when the archive's details could not be read */
  records: number | null;
  includesMedia: boolean | null;
  mediaFiles: number | null;
  storage: { type: 'local' | 's3'; location: string };
  /** Where the numbers came from, so the screen can be honest about them */
  metadataSource: 'sidecar' | 'archive' | 'none';
  error?: string;
}

export interface CreateBackupResult {
  success: boolean;
  backupName?: string;
  size?: number;
  error?: string;
  metadata?: {
    totalRecords: number;
    includesMedia: boolean;
    media: { included: number; bytes: number; missing: { filePath: string; reason: string }[] };
    storage: { type: string; location: string };
  };
}

export interface BackupStats {
  totalBackups: number;
  totalSize: number;
  totalRecords: number;
  averageSize: number;
  oldestBackup: string | null;
  newestBackup: string | null;
}

export interface RestoreOptions {
  clearExisting?: boolean;
  skipUsers?: boolean;
  skipAuditLogs?: boolean;
  restoreMedia?: boolean;
}

export interface RestoreResult {
  success: boolean;
  restoredRecords: number;
  restoredFiles?: number;
  errors: string[];
  warnings: string[];
  summary: {
    [entityName: string]: {
      restored: number;
      errors: number;
    };
  };
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface BackupPreview {
  backupName: string;
  backupPath: string;
  totalRecords: number;
  entities: {
    [entityName: string]: {
      hasData: boolean;
      dataLength: number;
      entity: string;
      count: number;
      lastModified: string;
      sampleData: any[] | null;
      error: string | null;
    };
  };
  summary: {
    [entityName: string]: string;
  };
  warnings: string[];
  errors: string[];
}

export interface BackupPreviewResult {
  success: boolean;
  preview?: BackupPreview;
  error?: string;
}

export const backupService = {
  /**
   * Create a new backup
   */
  async createBackup(options: { includeMedia?: boolean } = {}): Promise<CreateBackupResult> {
    // Archives are kept in storage; this returns what was written, not the file
    const response = await apiService.post('/api/backup/create', options, {
      timeout: 15 * 60 * 1000,
    });
    return response.data;
  },

  /**
   * List all available backups
   */
  async listBackups(): Promise<{ success: boolean; backups: BackupInfo[] }> {
    const response = await apiService.get('/api/backup/list');
    return response.data;
  },

  /**
   * Get backup information
   */
  async getBackupInfo(backupName: string): Promise<{ success: boolean; info?: any; error?: string }> {
    const response = await apiService.get(`/api/backup/info/${encodeURIComponent(backupName)}`);
    return response.data;
  },

  /**
   * Delete a backup
   */
  async deleteBackup(backupName: string): Promise<{ success: boolean; error?: string }> {
    const response = await apiService.delete(`/api/backup/${encodeURIComponent(backupName)}`);
    return response.data;
  },

  /**
   * Download a backup
   */
  async downloadBackup(backupName: string): Promise<Blob> {
    const response = await apiService.get(`/api/backup/download/${encodeURIComponent(backupName)}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Upload a backup file
   */
  async uploadBackup(file: File): Promise<{ success: boolean; fileName?: string; error?: string; validation?: ValidationResult }> {
    const formData = new FormData();
    formData.append('backup', file);

    const response = await apiService.post('/api/backup/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Restore from a backup
   */
  async restoreBackup(backupName: string, options: RestoreOptions = {}): Promise<RestoreResult> {
    // The backend refuses a restore that isn't explicitly confirmed
    const response = await apiService.post(
      `/api/backup/restore/${encodeURIComponent(backupName)}`,
      { ...options, confirm: 'restore' },
      { timeout: 30 * 60 * 1000 },
    );
    return response.data;
  },

  /**
   * Validate a backup
   */
  async validateBackup(backupName: string): Promise<ValidationResult> {
    const response = await apiService.post(`/api/backup/validate/${encodeURIComponent(backupName)}`);
    return response.data;
  },

  /**
   * Preview backup contents
   */
  async previewBackup(backupName: string): Promise<BackupPreviewResult> {
    const response = await apiService.get(`/api/backup/preview/${encodeURIComponent(backupName)}`);
    return response.data;
  },

  /**
   * Get backup statistics
   */
  async getBackupStats(): Promise<{ success: boolean; stats: BackupStats }> {
    const response = await apiService.get('/api/backup/stats');
    return response.data;
  },

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString();
  },
};
